// src/pages/admin/components/PropertyMediaManager.jsx

import React from 'react';
import { AdminAPI } from '../../../api/admin';
import { Card, Actions, Btn, Field, Badge } from '../ui';
import MediaPickerModal from './MediaPickerModal';
import { normalizeMediaUrl } from '../../../api/base';

export default function PropertyMediaManager({ propertyId, propertyType = 'regular' }) {
  const [items, setItems] = React.useState([]); // [{id, media_id, url, thumbnail_url, is_main, is_active, sort_order}]
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [openPicker, setOpenPicker] = React.useState(false);
  const [dragId, setDragId] = React.useState(null);
  const [uploadProgress, setUploadProgress] = React.useState({ current: 0, total: 0 });
  const vizSuggestions = [
    { value: 'exterior', label: 'Экстерьер' },
    { value: 'interior', label: 'Интерьер' },
    { value: 'lobby', label: 'Лобби' },
    { value: 'infrastructure', label: 'Инфраструктура' },
    { value: 'environment', label: 'Окружение' },
    { value: 'plan', label: 'Планировки' },
    { value: 'floorplan', label: 'Планировки' },
    { value: 'layout', label: 'Планировки' },
    { value: 'map', label: 'Карта' },
    { value: 'view', label: 'Вид' },
    { value: 'video', label: 'Видео' },
    { value: '360', label: '360°' },
    { value: 'other', label: 'Другое' }
  ];

  // Определяем API методы в зависимости от типа объекта
  const getApiMethods = () => {
    switch (propertyType) {
      case 'foreign':
        return {
          list: AdminAPI.listForeignPropertyMedia,
          attach: AdminAPI.attachForeignPropertyMedia,
          update: AdminAPI.updateForeignPropertyMedia,
          delete: AdminAPI.deleteForeignPropertyMedia,
          setMain: AdminAPI.setMainForeignPropertyMedia,
          reorder: AdminAPI.reorderForeignPropertyMedia,
        };
      case 'suburban':
        return {
          list: AdminAPI.listSuburbanPropertyMedia,
          attach: AdminAPI.attachSuburbanPropertyMedia,
          update: AdminAPI.updateSuburbanPropertyMedia,
          delete: AdminAPI.deleteSuburbanPropertyMedia,
          setMain: AdminAPI.setMainSuburbanPropertyMedia,
          reorder: AdminAPI.reorderSuburbanPropertyMedia,
        };
      case 'commercial-rent':
        return {
          list: AdminAPI.listCommercialRentPropertyMedia,
          attach: AdminAPI.attachCommercialRentPropertyMedia,
          update: AdminAPI.updateCommercialRentPropertyMedia,
          delete: AdminAPI.deleteCommercialRentPropertyMedia,
          setMain: AdminAPI.setMainCommercialRentPropertyMedia,
          reorder: AdminAPI.reorderCommercialRentPropertyMedia,
        };
      case 'parking':
        return {
          list: AdminAPI.listParkingPropertyMedia,
          attach: AdminAPI.attachParkingPropertyMedia,
          update: AdminAPI.updateParkingPropertyMedia,
          delete: AdminAPI.deleteParkingPropertyMedia,
          setMain: AdminAPI.setMainParkingPropertyMedia,
          reorder: AdminAPI.reorderParkingPropertyMedia,
        };
      default:
        return {
          list: AdminAPI.listPropertyMedia,
          attach: AdminAPI.attachPropertyMedia,
          update: AdminAPI.updatePropertyMedia,
          delete: AdminAPI.deletePropertyMedia,
          setMain: AdminAPI.setMainPropertyMedia,
          reorder: AdminAPI.reorderPropertyMedia,
        };
    }
  };

  const api = React.useMemo(() => getApiMethods(), [propertyType]);

  const load = React.useCallback(async ()=>{
    setBusy(true);
    setMsg('');
    try{
      const resp = await api.list(propertyId);
      // Поддерживаем разные форматы ответов
      const imgs = Array.isArray(resp?.images) ? resp.images : Array.isArray(resp) ? resp : [];
      setItems(
        imgs.map(x => ({
          ...x,
          url:           normalizeMediaUrl(x.media?.url || x.url),
          thumbnail_url: normalizeMediaUrl(x.media?.thumbnail_url || x.thumbnail_url),
          display_url:   normalizeMediaUrl(x.media?.display_url || x.display_url),
        }))
      );
    }catch(e){ 
      console.error('Error loading media:', e);
      setMsg(e.message || 'Не удалось загрузить фото'); 
    }
    finally{ setBusy(false); }
  }, [propertyId, api, propertyType]);

  React.useEffect(()=>{ load(); }, [load]);

  const addFromLibrary = async (picked /* array of media objects from modal */) => {
    setBusy(true);
    try {
      // если нет главной — первую делаем главной
      const hasMain = items.some(x => x.is_main);
      const hasHeroSlider = items.some(x => x.is_hero_slider);
      
      for (let i = 0; i < picked.length; i++) {
        // attach
        await api.attach(propertyId, {
          media_id: picked[i].id,
          is_main: !hasMain && i === 0,
          is_hero_slider: !hasHeroSlider && i === 0, // первое фото автоматически в слайдер, если слайдер пуст
          sort_order: (items.length + i),
          is_active: true,
        });
      }
      await load();
      setMsg(`Добавлено ${picked.length} фотографий`);
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      console.error('Error adding from library:', e);
      setMsg(e.message || 'Не удалось привязать медиа');
      setTimeout(() => setMsg(''), 5000);
    } finally {
      setBusy(false);
    }
  };

  const uploadAndAttachMany = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setBusy(true);
    setUploadProgress({ current: 0, total: files.length });
    setMsg(`Загрузка началась: 0 из ${files.length}`);
    
    try {
      let order = items.length;
      const hasMain = items.some(x => x.is_main);
      const hasHeroSlider = items.some(x => x.is_hero_slider);
      
      for (let i = 0; i < files.length; i++) {
        setUploadProgress({ current: i + 1, total: files.length });
        setMsg(`Загрузка: ${i + 1} из ${files.length}`);
        
        const up = await AdminAPI.uploadMedia(files[i]);
        const media = up.file || up;
        await api.attach(propertyId, {
          media_id: media.id,
          is_main: !hasMain && i === 0 && order === items.length, // если не было главной, первая загружаемая станет главной
          is_hero_slider: !hasHeroSlider && i === 0, // первое фото автоматически в слайдер, если слайдер пуст
          sort_order: order++,
          is_active: true,
        });
      }
      await load();
      setMsg(`✅ Загружено ${files.length} фотографий`);
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      console.error('Error uploading files:', e);
      setMsg(`❌ ${e.message || 'Загрузка не удалась'}`);
      setTimeout(() => setMsg(''), 5000);
    } finally {
      setBusy(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const setMain = async (link) => {
    setBusy(true);
    try {
      // Используем обычный PUT запрос с is_main: true
      await api.update(propertyId, link.id, { is_main: true });
      
      // Автоматически добавляем главную картинку в слайдер, если она там не была
      if (!link.is_hero_slider) {
        await api.update(propertyId, link.id, { 
          is_main: true, 
          is_hero_slider: true 
        });
      }
      
      await load();
      setMsg('Главная фотография установлена');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      console.error('Error setting main photo:', e);
      setMsg(e.message || 'Не удалось установить главную');
      setTimeout(() => setMsg(''), 5000);
    } finally { 
      setBusy(false); 
    }
  };

  const toggleActive = async (link) => {
    const next = !link.is_active;
    setItems(prev => prev.map(x => x.id === link.id ? { ...x, is_active: next } : x));
    try{
      await api.update(propertyId, link.id, { is_active: next });
      setMsg(next ? 'Фотография показана' : 'Фотография скрыта');
      setTimeout(() => setMsg(''), 3000);
    }catch(e){
      console.error('Error toggling active status:', e);
      setMsg(e.message || 'Не удалось обновить статус');
      setTimeout(() => setMsg(''), 5000);
      await load();
    }
  };

  const toggleHeroSlider = async (link) => {
    if (!link || !link.id) {
      console.error('Invalid link object:', link);
      setMsg('Ошибка: неправильный ID медиа');
      setTimeout(() => setMsg(''), 3000);
      return;
    }
    
    const next = !link.is_hero_slider;
    setItems(prev => prev.map(x => x.id === link.id ? { ...x, is_hero_slider: next } : x));
    
    try{
      await api.update(propertyId, link.id, { is_hero_slider: next });
      
      // Если добавляем в слайдер и нет главной картинки, делаем эту главной
      if (next && !items.some(x => x.is_main)) {
        await api.update(propertyId, link.id, { 
          is_hero_slider: true, 
          is_main: true 
        });
      }
      
      await load();
      setMsg(next ? 'Добавлено в главный слайдер' : 'Убрано из главного слайдера');
      setTimeout(() => setMsg(''), 3000);
    }catch(e){
      console.error('Error toggling hero slider:', e);
      setMsg(e.message || 'Не удалось обновить статус главного слайдера');
      setTimeout(() => setMsg(''), 5000);
      await load();
    }
  };

  const updateLink = async (linkId, patch) => {
    // оптимистично обновляем состояние
    setItems(prev => prev.map(x => x.id === linkId ? { ...x, ...patch } : x));
    try {
      await api.update(propertyId, linkId, patch);
      setMsg('Настройки обновлены');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      console.error('Error updating link:', e);
      setMsg(e.message || 'Не удалось сохранить');
      setTimeout(() => setMsg(''), 5000);
      await load();
    }
  };

  const remove = async (link) => {
    if (!window.confirm('Удалить изображение из галереи? Файл из медиатеки не удаляется.')) return;
    try{
      await api.delete(propertyId, link.id);
      await load();
      setMsg('Фотография удалена из галереи');
      setTimeout(() => setMsg(''), 3000);
    }catch(e){ 
      console.error('Error removing photo:', e);
      setMsg(e.message || 'Не удалось удалить');
      setTimeout(() => setMsg(''), 5000);
    }
  };

  // --- DND сортировка ---
  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) return;
    const arr = [...items];
    const from = arr.findIndex(x => x.id === dragId);
    const to   = arr.findIndex(x => x.id === targetId);
    if (from < 0 || to < 0) return;

    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setItems(arr);

    try{
      await AdminAPI.reorderPropertyMedia(propertyId, arr.map(x => x.id));
    }catch(e){
      alert(e.message || 'Не удалось сохранить порядок');
      await load();
    } finally {
      setDragId(null);
    }
  };

  return (
    <Card>
      <h3>
        Фотографии объекта
        {items.length > 0 && (
          <span style={{marginLeft: 8, fontSize: 14, opacity: 0.7}}>
            (всего: {items.length}, в главном слайдере: {items.filter(x => x.is_hero_slider).length})
          </span>
        )}
      </h3>
      {msg && <div style={{ color:'#888', marginBottom:8 }}>{msg}</div>}
      {uploadProgress.total > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ 
            width: '100%', 
            height: 8, 
            background: '#e0e0e0', 
            borderRadius: 4, 
            overflow: 'hidden' 
          }}>
            <div style={{ 
              width: `${(uploadProgress.current / uploadProgress.total) * 100}%`, 
              height: '100%', 
              background: 'var(--gold)', 
              transition: 'width 0.3s ease' 
            }} />
          </div>
        </div>
      )}

      <Actions style={{gap:8, flexWrap:'wrap'}}>
        <Field>Загрузить выбранные
          <input type="file" accept="image/*" multiple onChange={e=>uploadAndAttachMany(e.target.files)}/>
        </Field>
        <Btn onClick={()=>setOpenPicker(true)}>Добавить из библиотеки</Btn>
        <Btn 
          onClick={async()=>{
            if (!window.confirm('Добавить все активные фото в главный слайдер?')) return;
            setBusy(true);
            try {
              for (const item of items.filter(x => x.is_active)) {
                await api.update(propertyId, item.id, { is_hero_slider: true });
              }
              await load();
            } catch (e) {
              alert(e.message || 'Ошибка');
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
        >
          Все в слайдер
        </Btn>
        <Btn 
          onClick={async()=>{
            if (!window.confirm('Убрать все фото из главного слайдера?')) return;
            setBusy(true);
            try {
              for (const item of items.filter(x => x.is_hero_slider)) {
                await api.update(propertyId, item.id, { is_hero_slider: false });
              }
              await load();
            } catch (e) {
              alert(e.message || 'Ошибка');
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
        >
          Очистить слайдер
        </Btn>
        <Btn 
          onClick={async()=>{
            if (!items.length) {
              setMsg('Нет фотографий для удаления');
              setTimeout(() => setMsg(''), 3000);
              return;
            }
            if (!window.confirm(`Удалить ВСЕ фотографии (${items.length} шт.) из галереи объекта? Файлы из медиатеки не удаляются.`)) return;
            setBusy(true);
            setMsg(`Удаление ${items.length} фотографий...`);
            try {
              for (const item of items) {
                await api.delete(propertyId, item.id);
              }
              await load();
              setMsg('✅ Все фотографии удалены из галереи');
              setTimeout(() => setMsg(''), 3000);
            } catch (e) {
              console.error('Error deleting all photos:', e);
              setMsg(`❌ ${e.message || 'Не удалось удалить все фото'}`);
              setTimeout(() => setMsg(''), 5000);
              await load();
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy || items.length === 0}
          style={{background:'#f44336', color:'white'}}
        >
          Удалить все фото
        </Btn>
        <Badge>Всего: <b>{items.length}</b></Badge>
      </Actions>

      {busy && <div style={{marginTop:8}}>Обновление…</div>}

      <div style={grid}>
        {items.map(link => {
          const url = link.thumbnail_url || link.url;
          return (
            <div
              key={link.id}
              draggable
              onDragStart={()=>onDragStart(link.id)}
              onDragOver={onDragOver}
              onDrop={()=>onDrop(link.id)}
              title={`link #${link.id}`}
              style={{...tile, opacity: link.is_active ? 1 : 0.55, borderColor: link.is_main ? 'var(--primary)' : '#ddd'}}
            >
              {url ? <img src={url} alt="" style={img} /> : <div style={empty}>no preview</div>}
              <div style={{display:'flex', alignItems:'center', gap:6, flexWrap:'wrap'}}>
                <div style={{display:'flex', gap:6, alignItems:'center', flexWrap:'wrap'}}>
                  {link.is_main && <Badge $variant="primary">Главная</Badge>}
                  {link.is_hero_slider && <Badge style={{background:'#4caf50', color:'white'}}>Главный слайдер</Badge>}
                  {!link.is_active && <Badge>скрыто</Badge>}
                  {!!link.viz_type && <Badge title="Тип визуализации">{link.viz_type}</Badge>}
                  <small style={{color:'#777'}}>#{link.id}</small>
                </div>
                <div style={{display:'flex', gap:6, flexWrap:'wrap', marginLeft:'auto'}}>
                  {!link.is_main ? (
                    <Btn onClick={()=>setMain(link)}>Сделать главной</Btn>
                  ) : (
                    <Btn 
                      onClick={()=>api.update(propertyId, link.id, { is_main: false }).then(load)}
                      style={{background:'#f44336', color:'white'}}
                    >
                      Убрать из главной
                    </Btn>
                  )}
                  <Btn 
                    onClick={()=>toggleHeroSlider(link)}
                    style={link.is_hero_slider ? {background:'#4caf50', color:'white'} : undefined}
                  >
                    {link.is_hero_slider ? 'Убрать из слайдера' : 'В главный слайдер'}
                  </Btn>
                  <Btn onClick={()=>toggleActive(link)}>{link.is_active ? 'Скрыть' : 'Показать'}</Btn>
                  <Btn onClick={()=>remove(link)}>Удалить</Btn>
                </div>
              </div>

              {/* Визуализация: только тип */}
              <div style={{marginTop:4}}>
                <label style={{fontSize:12}}>Тип визуализации
                  <select
                    value={link.viz_type || ''}
                    onChange={(e)=>{
                      const val = e.target.value;
                      updateLink(link.id, { viz_type: val, viz_type_slug: val });
                    }}
                  >
                    <option value="">—</option>
                    {vizSuggestions.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </label>
              </div>
            </div>
          );
        })}
      </div>

      <MediaPickerModal
        open={openPicker}
        onClose={()=>setOpenPicker(false)}
        onPick={addFromLibrary}
        multi
      />
    </Card>
  );
}

const grid = { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:12, marginTop:10 };
const tile = { border:'2px solid #ddd', borderRadius:12, padding:8, background:'#fff', display:'flex', flexDirection:'column', gap:8, cursor:'grab' };
const img  = { width:'100%', height:180, objectFit:'cover', borderRadius:8, background:'#f3f3f3' };
const empty= { display:'grid', placeItems:'center', width:'100%', height:180, background:'#f5f5f5', borderRadius:8, color:'#888' };
